import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import Link from '@/routing/Link'
import { ArrowRight, Loader2, Mail } from '@digital-twins/design-system/icons'
import { authApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { safeReturnTo } from '@/lib/utils/safe-return-to'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { SocialLogin } from '@/components/auth/SocialLogin'
import { PasswordInput } from '@/components/auth/PasswordInput'

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const { loginWithResponse } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const registerParams = new URLSearchParams()
  if (email) registerParams.set('email', email)
  const returnTo = searchParams.get('returnTo')
  if (returnTo) registerParams.set('returnTo', returnTo)
  const registerHref = registerParams.toString()
    ? `/register?${registerParams.toString()}`
    : '/register'

  useEffect(() => {
    // Redirect legacy org-invite links (/login?tab=register&...) to /register
    if (searchParams.get('tab') === 'register') {
      const forwarded = new URLSearchParams(searchParams.toString())
      forwarded.delete('tab')
      router.replace(`/register?${forwarded.toString()}`)
      return
    }

    const errorParam = searchParams.get('error')
    if (errorParam === 'auth_callback_error') {
      setError(t('login.errors.authCallback'))
    }
    if (errorParam === 'sso_registration_expired') {
      setError(t('login.errors.ssoExpired'))
    }

    const emailParam = searchParams.get('email')
    if (emailParam) setEmail(emailParam)
  }, [searchParams, router, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await authApi.login(email, password)
      loginWithResponse(response)
      router.push(safeReturnTo(returnTo))
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('login.errors.invalidCredentials')))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Heading level={1} size="3xl" className="text-center font-display font-normal tracking-tight">
          {t('login.title')}
        </Heading>
      </div>

      {searchParams.get('invite') && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 size-5 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-foreground">{t('login.inviteTitle')}</p>
              <Text as="p" size="sm" color="muted" className="mt-1">
                {t('login.inviteBody')}
              </Text>
            </div>
          </div>
        </div>
      )}

      <SocialLogin />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('email', { ns: 'common' })}</Label>
          <Input
            id="email"
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('emailPlaceholder')}
            required
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t('password', { ns: 'common' })}</Label>
            <Link href="/forgot-password" className="text-sm text-primary hover:text-primary/80 transition-colors py-2 px-1 -my-2 -mr-1 inline-block">
              {t('login.forgotPassword')}
            </Link>
          </div>
          <PasswordInput
            id="password"
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            minLength={8}
            required
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div data-testid="login-error" className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" variant="cta" data-testid="login-submit" disabled={loading} className="w-full gap-2 text-base">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('login.submitting')}
            </>
          ) : (
            <>
              {t('login.submit')}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </form>

      <Text as="p" size="sm" color="muted" className="text-center">
        {t('login.noAccount')}{' '}
        <Button variant="link" size="sm" className="h-auto p-0 text-sm" asChild>
          <Link href={registerHref}>{t('login.createAccount')}</Link>
        </Button>
      </Text>
    </div>
  )
}
